import { call, put, select, takeLatest, takeEvery, all, delay, race } from 'redux-saga/effects';
import * as T from './ProductCatalogChatbotConstants';
import * as actions from './ProductCatalogChatbotActions';
import type { PCActionCard } from '../../../interface/ProductCatalogChatbotInterface';
import type { ProductCatalogChatbotState } from './ProductCatalogChatbotReducer';
import {
    createConversationApi,
    listConversationsApi,
    getConversationApi,
    sendMessageApi,
    renameConversationApi,
    deleteConversationApi,
    confirmCardApi,
    cancelCardApi,
    pickCandidateApi,
    setCardChannelsApi,
    setCardDurationApi,
    removeCardRowApi,
    fetchChannelsApi,
    runSyncApi,
    fetchSyncResultsApi,
    retrySyncApi,
    fetchLogApi,
    undoLogEntryApi,
    refreshCatalogueApi,
    fetchCtasApi,
} from './ProductCatalogChatbotApi';

const errMsg = (err: any, fallback: string): string => err?.message || fallback;

// -- Staged "thinking" status text -------------------------------------------------
// Purely presentational — timed to typical latency so the wait reads as active
// progress rather than a freeze. Ported from the reference `useBrisqueAssistant`
// hook's SEND_STAGES/CONFIRM_STAGES; here the sequence runs as a saga `race()`
// against the real request instead of component setTimeouts, since the saga
// already owns the request lifecycle (sending/cardActionLoading).
const SEND_STAGES: { delay: number; label: string }[] = [
    { delay: 0, label: 'Understanding request…' },
    { delay: 900, label: 'Planning actions…' },
    { delay: 2200, label: 'Checking menu state…' },
];

const CONFIRM_STAGES: { delay: number; label: string }[] = [
    { delay: 0, label: 'Applying changes…' },
    { delay: 900, label: 'Updating menu state…' },
];

/** Advance `thinkingStatus` through `stages` on their absolute-offset delays, then idle —
 *  the caller's `race()` cancels this task the instant the real request settles. Delays in the
 *  tables are "ms since the stage sequence started"; this walks them by waiting only the
 *  incremental gap between consecutive stages. */
function* runThinkingStages(stages: { delay: number; label: string }[]) {
    yield put(actions.setThinkingStatus(stages[0].label));
    let elapsed = 0;
    for (let i = 1; i < stages.length; i += 1) {
        yield delay(stages[i].delay - elapsed);
        elapsed = stages[i].delay;
        yield put(actions.setThinkingStatus(stages[i].label));
    }
    // Idle on the last label until cancelled. This MUST never return: it is raced
    // against the real request, so completing here would win the race and cancel
    // the request — the response would be lost and nothing would render, while the
    // write had already landed server-side. Held open in bounded sleeps rather than
    // one huge delay, since setTimeout overflows past ~24.8 days and fires at once.
    while (true) {
        yield delay(60000);
    }
}

// -- Sync countdown -----------------------------------------------------------------
const SYNC_WAIT_SECONDS = 15;

/** Tick the sync card's countdown down to 0, then fetch the real outcome — one continuous
 *  flow so `takeLatest` can cancel the whole thing (countdown included) if the user retries
 *  or navigates away mid-wait. */
function* runSyncCountdown(identity: any, conversationId: string, msgId: string) {
    for (let remaining = SYNC_WAIT_SECONDS; remaining > 0; remaining -= 1) {
        yield put(actions.syncTick({ msgId, remaining }));
        yield delay(1000);
    }
    // Wire response is { card: PCSyncCard } — never the bare card. Pass msgId so the backend
    // persists the results card onto the stream message (survives reload, §Item 2).
    const data: any = yield call(fetchSyncResultsApi, identity, conversationId, msgId);
    yield put(actions.syncResultsSuccess({ msgId, card: data?.card }));
    yield put(actions.fetchLogRequest({ identity }));
}

// -- Optimistic card patch + rollback -----------------------------------------------
/** The current card with this id, or undefined — used to capture a pre-mutation snapshot for
 *  rollback before an optimistic patch. */
function* selectCard(cardId: string): Generator<any, PCActionCard | undefined, any> {
    const state: ProductCatalogChatbotState = yield select((s: any) => s.ProductCatalogChatbot);
    const msg = state.conversation?.messages.find((m) => m.kind === 'action' && m.card.id === cardId);
    return msg && msg.kind === 'action' ? msg.card : undefined;
}

// ── Conversations ───────────────────────────────────────────────────────────────

function* createConversationSaga(action: ReturnType<typeof actions.createConversationRequest>) {
    const { identity, fresh, title } = action.payload;
    try {
        const data: any = yield call(createConversationApi, identity, { fresh, title });
        yield put(actions.createConversationSuccess(data));
        // A brand-new thread won't be in the sidebar yet — refresh it. (A plain
        // resume doesn't add anything, so it doesn't need the extra call.)
        if (fresh) yield put(actions.listConversationsRequest({ identity }));
    } catch (err: any) {
        yield put(actions.createConversationFailure(errMsg(err, 'Failed to start a conversation')));
    }
}

function* listConversationsSaga(action: ReturnType<typeof actions.listConversationsRequest>) {
    try {
        const data: any = yield call(listConversationsApi, action.payload.identity);
        yield put(actions.listConversationsSuccess(data?.conversations ?? []));
    } catch (err: any) {
        yield put(actions.listConversationsFailure(errMsg(err, 'Failed to load conversations')));
    }
}

function* loadConversationSaga(action: ReturnType<typeof actions.loadConversationRequest>) {
    const { identity, conversationId } = action.payload;
    try {
        const data: any = yield call(getConversationApi, identity, conversationId);
        yield put(actions.loadConversationSuccess(data));
    } catch (err: any) {
        yield put(actions.loadConversationFailure(errMsg(err, 'Failed to load the conversation')));
    }
}

function* sendMessageSaga(action: ReturnType<typeof actions.sendMessageRequest>) {
    const { identity, conversationId, text } = action.payload;
    try {
        // The FIRST message of a thread auto-titles it server-side — predict that locally,
        // instantly, so the header doesn't sit on "New chat" until the next full reload.
        const state: ProductCatalogChatbotState = yield select((s: any) => s.ProductCatalogChatbot);
        if (state.conversation?.conversationId === conversationId && state.conversation.messages.length === 0) {
            const trimmed = text.trim();
            yield put(actions.predictTitle(trimmed.length > 60 ? trimmed.slice(0, 57).trimEnd() + '…' : trimmed));
        }
        const { data }: any = yield race({
            data: call(sendMessageApi, identity, conversationId, text),
            stages: runThinkingStages(SEND_STAGES),
        });
        if (!data) throw new Error('The assistant did not respond. Please try again.');
        yield put(actions.sendMessageSuccess({ conversationId, messages: data.messages ?? [] }));
    } catch (err: any) {
        yield put(actions.sendMessageFailure(errMsg(err, 'Failed to send your message')));
    } finally {
        yield put(actions.setThinkingStatus(null));
    }
}

function* renameConversationSaga(action: ReturnType<typeof actions.renameConversationRequest>) {
    const { identity, conversationId, title } = action.payload;
    try {
        yield call(renameConversationApi, identity, conversationId, title);
        yield put(actions.renameConversationSuccess({ conversationId, title }));
    } catch (err: any) {
        yield put(actions.renameConversationFailure(errMsg(err, 'Failed to rename the conversation')));
    }
}

function* deleteConversationSaga(action: ReturnType<typeof actions.deleteConversationRequest>) {
    const { identity, conversationId } = action.payload;
    try {
        yield call(deleteConversationApi, identity, conversationId);
        yield put(actions.deleteConversationSuccess({ conversationId }));
    } catch (err: any) {
        yield put(actions.deleteConversationFailure(errMsg(err, 'Failed to delete the conversation')));
    }
}

// ── Card actions ─────────────────────────────────────────────────────────────────

function* confirmCardSaga(action: ReturnType<typeof actions.confirmCardRequest>) {
    const { identity, cardId } = action.payload;
    try {
        const { data }: any = yield race({
            data: call(confirmCardApi, identity, cardId),
            stages: runThinkingStages(CONFIRM_STAGES),
        });
        if (!data) throw new Error('The change may have been applied, but the assistant did not respond. Refresh to check.');
        yield put(actions.confirmCardSuccess({ conversationId: data.conversationId, messages: data.messages ?? [] }));
        // A commit writes a new entry to the user's activity log — refresh it.
        yield put(actions.fetchLogRequest({ identity }));
    } catch (err: any) {
        yield put(actions.confirmCardFailure(errMsg(err, 'Failed to confirm the change')));
    } finally {
        yield put(actions.setThinkingStatus(null));
    }
}

function* cancelCardSaga(action: ReturnType<typeof actions.cancelCardRequest>) {
    const { identity, cardId } = action.payload;
    try {
        const data: any = yield call(cancelCardApi, identity, cardId);
        yield put(actions.cancelCardSuccess(data?.card));
    } catch (err: any) {
        yield put(actions.cancelCardFailure(errMsg(err, 'Failed to cancel the change')));
    }
}

function* pickCandidateSaga(action: ReturnType<typeof actions.pickCandidateRequest>) {
    const { identity, cardId, candidateId } = action.payload;
    // Optimistically retire the picker so its buttons vanish the instant you tap — the
    // answered question shouldn't stay clickable while the fresh card loads in below.
    const prev: PCActionCard | undefined = yield call(selectCard, cardId);
    yield put(actions.optimisticCardPatch({ cardId, patch: { status: 'cancelled', candidates: undefined } }));
    try {
        // Backend appends messages (retired picker + fresh confirm card + optional next queued
        // picker) rather than returning a single { card } — handle it exactly like confirm.
        const { data }: any = yield race({
            data: call(pickCandidateApi, identity, cardId, candidateId),
            stages: runThinkingStages(SEND_STAGES),
        });
        if (!data) throw new Error('The assistant did not respond. Please try again.');
        yield put(actions.pickCandidateSuccess({ conversationId: data.conversationId, messages: data.messages ?? [] }));
    } catch (err: any) {
        if (prev) yield put(actions.optimisticCardPatch({ cardId, patch: prev }));
        yield put(actions.pickCandidateFailure(errMsg(err, 'Failed to pick that option')));
    } finally {
        yield put(actions.setThinkingStatus(null));
    }
}

function* setCardChannelsSaga(action: ReturnType<typeof actions.setCardChannelsRequest>) {
    const { identity, cardId, rowId, channels } = action.payload;
    const prev: PCActionCard | undefined = yield call(selectCard, cardId);
    if (prev) {
        yield put(actions.optimisticCardPatch({
            cardId,
            patch: { rows: prev.rows.map((r) => (r.id === rowId ? { ...r, channels } : r)) },
        }));
    }
    try {
        const data: any = yield call(setCardChannelsApi, identity, cardId, rowId, channels);
        yield put(actions.setCardChannelsSuccess(data?.card));
    } catch (err: any) {
        if (prev) yield put(actions.optimisticCardPatch({ cardId, patch: prev }));
        yield put(actions.setCardChannelsFailure(errMsg(err, 'Failed to update channels')));
    }
}

function* setCardDurationSaga(action: ReturnType<typeof actions.setCardDurationRequest>) {
    const { identity, cardId, rowId, duration } = action.payload;
    const prev: PCActionCard | undefined = yield call(selectCard, cardId);
    if (prev) {
        yield put(actions.optimisticCardPatch({
            cardId,
            patch: { rows: prev.rows.map((r) => (r.id === rowId ? { ...r, duration } : r)) },
        }));
    }
    try {
        const data: any = yield call(setCardDurationApi, identity, cardId, rowId, duration);
        yield put(actions.setCardDurationSuccess(data?.card));
    } catch (err: any) {
        if (prev) yield put(actions.optimisticCardPatch({ cardId, patch: prev }));
        yield put(actions.setCardDurationFailure(errMsg(err, 'Failed to update duration')));
    }
}

function* removeCardRowSaga(action: ReturnType<typeof actions.removeCardRowRequest>) {
    const { identity, cardId, rowId } = action.payload;
    const prev: PCActionCard | undefined = yield call(selectCard, cardId);
    if (prev) {
        yield put(actions.optimisticCardPatch({ cardId, patch: { rows: prev.rows.filter((r) => r.id !== rowId) } }));
    }
    try {
        const data: any = yield call(removeCardRowApi, identity, cardId, rowId);
        yield put(actions.removeCardRowSuccess(data?.card));
    } catch (err: any) {
        if (prev) yield put(actions.optimisticCardPatch({ cardId, patch: prev }));
        yield put(actions.removeCardRowFailure(errMsg(err, 'Failed to remove the row')));
    }
}

// ── Channels ───────────────────────────────────────────────────────────────────

function* fetchChannelsSaga(action: ReturnType<typeof actions.fetchChannelsRequest>) {
    try {
        const data: any = yield call(fetchChannelsApi, action.payload.identity);
        yield put(actions.fetchChannelsSuccess(data?.channels ?? []));
    } catch (err: any) {
        yield put(actions.fetchChannelsFailure(errMsg(err, 'Failed to load channels')));
    }
}

// ── Sync ─────────────────────────────────────────────────────────────────────────

function* runSyncSaga(action: ReturnType<typeof actions.runSyncRequest>) {
    const { identity, conversationId, msgId } = action.payload;
    try {
        // Wire response is { card: PCSyncCard } — never the bare card. Pass msgId so the
        // backend persists the 'running' card onto the stream message (survives reload).
        const data: any = yield call(runSyncApi, identity, conversationId, msgId);
        const card = data?.card;
        yield put(actions.runSyncSuccess({ msgId, card }));
        // A 'running' card means the trigger fired but the outcome isn't known yet — chain
        // straight into the countdown + results fetch as one continuous flow.
        if (card?.status === 'running') {
            yield call(runSyncCountdown, identity, conversationId, msgId);
        }
    } catch (err: any) {
        yield put(actions.runSyncFailure(errMsg(err, 'Failed to start the sync')));
    }
}

function* syncResultsSaga(action: ReturnType<typeof actions.syncResultsRequest>) {
    const { identity, conversationId, msgId } = action.payload;
    try {
        const data: any = yield call(fetchSyncResultsApi, identity, conversationId, msgId);
        yield put(actions.syncResultsSuccess({ msgId, card: data?.card }));
        // Sync outcome changes each entry's syncStatus — refresh the log.
        yield put(actions.fetchLogRequest({ identity }));
    } catch (err: any) {
        yield put(actions.syncResultsFailure(errMsg(err, 'Failed to fetch sync results')));
    }
}

function* syncRetrySaga(action: ReturnType<typeof actions.syncRetryRequest>) {
    const { identity, conversationId, msgId, previous } = action.payload;
    try {
        const data: any = yield call(retrySyncApi, identity, conversationId, previous, msgId);
        const card = data?.card;
        yield put(actions.syncRetrySuccess({ msgId, card }));
        if (card?.status === 'running') {
            yield call(runSyncCountdown, identity, conversationId, msgId);
        } else {
            // Retry changes each entry's syncStatus — refresh the log. (When the retry chains
            // into the countdown, runSyncCountdown's own results fetch already refreshes it.)
            yield put(actions.fetchLogRequest({ identity }));
        }
    } catch (err: any) {
        yield put(actions.syncRetryFailure(errMsg(err, 'Failed to retry the sync')));
    }
}

// ── Activity log & undo ───────────────────────────────────────────────────────────

function* fetchLogSaga(action: ReturnType<typeof actions.fetchLogRequest>) {
    try {
        const data: any = yield call(fetchLogApi, action.payload.identity);
        yield put(actions.fetchLogSuccess(data?.entries ?? []));
    } catch (err: any) {
        yield put(actions.fetchLogFailure(errMsg(err, 'Failed to load your activity log')));
    }
}

/** The thread an undo should be written into: whatever is open, else the most recently
 *  updated thread in the sidebar list. The activity log is location-wide, so the entry being
 *  undone may well have been made in a different (or since-deleted) thread — the reversal
 *  belongs in the conversation the user is actually looking at. */
function* latestConversationId(): any {
    const state: ProductCatalogChatbotState = yield select((s: any) => s.ProductCatalogChatbot);
    if (state.activeConversationId) return state.activeConversationId;
    const newest = [...state.conversations].sort(
        (a, b) => new Date(b.updatedAt ?? b.createdAt).getTime() - new Date(a.updatedAt ?? a.createdAt).getTime(),
    )[0];
    return newest?.conversationId ?? null;
}

function* undoLogEntrySaga(action: ReturnType<typeof actions.undoLogEntryRequest>) {
    const { identity, entryId } = action.payload;
    try {
        const conversationId: string | null = action.payload.conversationId ?? (yield call(latestConversationId));
        const result: any = yield call(undoLogEntryApi, identity, entryId, conversationId);
        yield put(actions.undoLogEntrySuccess({ entryId, result, conversationId }));
        // Undo is itself a change — refresh the log for the authoritative state.
        yield put(actions.fetchLogRequest({ identity }));
        // On success the backend has appended the reversal's messages to `result.conversationId`.
        // The reducer splices them into the open thread when it matches; when it doesn't (the
        // undo was routed to a thread we don't currently have loaded) pull that thread instead,
        // so the user lands on a complete conversation rather than a two-message fragment.
        const target = result?.conversationId ?? conversationId;
        if (result?.ok && target) {
            const state: ProductCatalogChatbotState = yield select((s: any) => s.ProductCatalogChatbot);
            if (state.conversation?.conversationId !== target) {
                yield put(actions.loadConversationRequest({ identity, conversationId: target }));
            }
        }
    } catch (err: any) {
        yield put(actions.undoLogEntryFailure(errMsg(err, 'Failed to undo that change')));
    }
}

// ── Quick-action CTAs (§Item 6) ─────────────────────────────────────────────────────

function* fetchCtasSaga(action: ReturnType<typeof actions.fetchCtasRequest>) {
    try {
        const data: any = yield call(fetchCtasApi, action.payload.identity);
        yield put(actions.fetchCtasSuccess(data?.ctas ?? []));
    } catch (err: any) {
        // Non-fatal — the intro falls back to its built-in prompts.
        yield put(actions.fetchCtasFailure(errMsg(err, 'Failed to load quick actions')));
    }
}

// ── Manual catalogue refresh ──────────────────────────────────────────────────────

function* refreshCatalogueSaga(action: ReturnType<typeof actions.refreshCatalogueRequest>) {
    try {
        const data: any = yield call(refreshCatalogueApi, action.payload.identity);
        yield put(actions.refreshCatalogueSuccess({ refreshedAt: data?.refreshedAt }));
    } catch (err: any) {
        yield put(actions.refreshCatalogueFailure(errMsg(err, 'Failed to refresh the catalogue')));
    }
}

// ── Root watcher ──────────────────────────────────────────────────────────────────

export default function* ProductCatalogChatbotSaga() {
    yield all([
        takeLatest(T.CREATE_CONVERSATION_REQUEST, createConversationSaga),
        takeLatest(T.LIST_CONVERSATIONS_REQUEST, listConversationsSaga),
        takeLatest(T.LOAD_CONVERSATION_REQUEST, loadConversationSaga),
        takeLatest(T.SEND_MESSAGE_REQUEST, sendMessageSaga),
        takeLatest(T.RENAME_CONVERSATION_REQUEST, renameConversationSaga),
        takeEvery(T.DELETE_CONVERSATION_REQUEST, deleteConversationSaga),
        takeLatest(T.CONFIRM_CARD_REQUEST, confirmCardSaga),
        takeLatest(T.CANCEL_CARD_REQUEST, cancelCardSaga),
        takeLatest(T.PICK_CANDIDATE_REQUEST, pickCandidateSaga),
        takeLatest(T.SET_CARD_CHANNELS_REQUEST, setCardChannelsSaga),
        takeLatest(T.SET_CARD_DURATION_REQUEST, setCardDurationSaga),
        takeLatest(T.REMOVE_CARD_ROW_REQUEST, removeCardRowSaga),
        takeLatest(T.FETCH_CHANNELS_REQUEST, fetchChannelsSaga),
        takeLatest(T.FETCH_CTAS_REQUEST, fetchCtasSaga),
        takeLatest(T.RUN_SYNC_REQUEST, runSyncSaga),
        takeLatest(T.SYNC_RESULTS_REQUEST, syncResultsSaga),
        takeLatest(T.SYNC_RETRY_REQUEST, syncRetrySaga),
        takeLatest(T.FETCH_LOG_REQUEST, fetchLogSaga),
        takeEvery(T.UNDO_LOG_ENTRY_REQUEST, undoLogEntrySaga),
        takeLatest(T.REFRESH_CATALOGUE_REQUEST, refreshCatalogueSaga),
    ]);
}
